import { DeliveryRuleField, type DeliveryRuleValue, deliveryRuleValid, modeOf } from "./DeliveryRule.js";
import { useMemo, useRef, useState } from "react";
import { moveItem, useDragReorder } from "../lib/useDragReorder.js";
import { type AgeBandUnit, ageToDays, daysToAgeValue, type Analyte, type AnalyteKind, type Branch, type BranchFormulaAnalyte, type BranchFormulaProblem, checkStudyFormulasByBranch, type FormulaProblem, type CatalogueSnapshot, studyAtBranch, type CommandType, intervalDisplay, type StudyBranchOverride, type StudyDetail, type StudyUpsertPayload } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { money } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { FormulaField } from "./FormulaField.js";
import { Icon } from "./icons.js";
import { Alert, Button, Chip, Field, IconButton, Input, Segmented, Select, Switch, Textarea } from "./ui.js";
import { AnalytePicker } from "./AnalytePicker.js";
import { RefText } from "./RefText.js";

/*
 * Catalogue editors (Administrador only; the commands enforce it, the pages hide the buttons).
 * Every save goes through `catalogue.study.upsert` or `catalogue.price.set`, which bump the
 * version; orders already registered keep the snapshot they were created with (ADR 0009).
 */

export type Lookups = Pick<CatalogueSnapshot, "areas" | "sample_types" | "containers" | "methods" | "techniques" | "equipment">;
export type LookupKey = keyof Lookups;
export type LookupRow = { id: string; name: string; is_active: boolean; sort_order?: number; colour_hex?: string };

const LOOKUP_COMMAND: Record<LookupKey, CommandType> = {
  areas: "catalogue.area.upsert",
  sample_types: "catalogue.sample_type.upsert",
  containers: "catalogue.container.upsert",
  methods: "catalogue.method.upsert",
  techniques: "catalogue.technique.upsert",
  equipment: "catalogue.equipment.upsert",
};

/** The editable part of a study, without analytes: a ficha-only save leaves them untouched. */
export function fichaPayload(s: StudyDetail): StudyUpsertPayload {
  return {
    study_id: s.id,
    clave: s.clave,
    name: s.name,
    kind: s.kind,
    area_id: s.area_id,
    sample_type_id: s.sample_type_id,
    container_id: s.container_id,
    method_id: s.method_id,
    technique_id: s.technique_id,
    equipment_id: s.equipment_id,
    indications: s.indications,
    delivery_days: s.delivery_days,
    delivery_by_weekday: s.delivery_by_weekday ?? null,
    is_active: s.is_active,
  };
}

const NEW = "__new";

/** A lookup select with "Agregar…" to create a new entry in place (the lookup's own upsert command). */
export function LookupSelect({ label, kind, rows, value, onChange, onCreated }: { label: string; kind: LookupKey; rows: LookupRow[]; value: string | null; onChange: (id: string | null) => void; onCreated: (row: LookupRow) => void }) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [colour, setColour] = useState("#9aa0a6");
  const [busy, setBusy] = useState(false);
  const options = rows.filter((r) => r.is_active || r.id === value);

  async function create() {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      const extra = kind === "containers" ? { colour_hex: colour } : kind === "areas" ? { sort_order: Math.max(0, ...rows.map((r) => r.sort_order ?? 0)) + 1 } : {};
      const row = await run<CommandType, LookupRow>(LOOKUP_COMMAND[kind], { id: null, name: n, is_active: true, ...extra } as never);
      onCreated(row);
      onChange(row.id);
      setAdding(false);
      setName("");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Field label={label} optional>
      <Select
        aria-label={label}
        value={adding ? NEW : (value ?? "")}
        onChange={(e) => {
          if (e.target.value === NEW) return setAdding(true);
          setAdding(false);
          onChange(e.target.value || null);
        }}
      >
        <option value="">Sin asignar</option>
        {options.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
            {r.is_active ? "" : " (inactivo)"}
          </option>
        ))}
        <option value={NEW}>Agregar…</option>
      </Select>
      {adding && (
        <span className="row" style={{ gap: 8 }}>
          <Input aria-label={`Nuevo: ${label}`} placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          {kind === "containers" && <input type="color" aria-label="Color del tapón" value={colour} onChange={(e) => setColour(e.target.value)} style={{ width: 48, height: 44 }} />}
          <Button size="sm" variant="primary" busy={busy} disabled={!name.trim()} onClick={() => void create()}>
            Agregar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
            Cancelar
          </Button>
        </span>
      )}
    </Field>
  );
}

type OverrideField = "method_id" | "technique_id" | "equipment_id";
type OverrideDraft = Record<OverrideField, string | null>;
const INHERIT: OverrideDraft = { method_id: null, technique_id: null, equipment_id: null };

/** Draft map → payload list: only branches that set at least one value; the rest inherit. */
export function overridesPayload(drafts: Record<string, OverrideDraft>): StudyBranchOverride[] {
  return Object.entries(drafts)
    .filter(([, d]) => d.method_id !== null || d.technique_id !== null || d.equipment_id !== null)
    .map(([branch_id, d]) => ({ branch_id, ...d }));
}

/** A per-branch select: empty = "Igual que el estudio", naming what that currently is. */
function OverrideSelect({ label, branch, rows, value, inherited, onChange }: { label: string; branch: Branch; rows: LookupRow[]; value: string | null; inherited: string | null; onChange: (id: string | null) => void }) {
  const options = rows.filter((r) => r.is_active || r.id === value);
  const inheritedName = inherited ? rows.find((r) => r.id === inherited)?.name : null;
  return (
    <Field label={label} optional>
      <Select aria-label={`${label} en ${branch.name}`} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{inheritedName ? `Igual que el estudio (${inheritedName})` : "Igual que el estudio"}</option>
        {options.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
            {r.is_active ? "" : " (inactivo)"}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/**
 * Ficha: name, lookups, delivery days, indications, active, and how each branch runs it. Clave
 * and kind stay as imported. A branch may use its own method/técnica/equipo (one QS, "enzimático
 * automatizado" at one branch and "química seca" at the other, instead of two studies); orders
 * snapshot the values of their own branch.
 */
export function StudyFichaForm({ study, lookups, branches = [], onSaved, onCancel }: { study: StudyDetail; lookups: Lookups; branches?: Branch[]; onSaved: (s: StudyDetail) => void; onCancel: () => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState(() => fichaPayload(study));
  const [rule, setRule] = useState<DeliveryRuleValue>({ delivery_days: study.delivery_days, delivery_by_weekday: study.delivery_by_weekday ?? null });
  const [daysText, setDaysText] = useState(study.delivery_days ? String(study.delivery_days) : "");
  const [extra, setExtra] = useState<Partial<Record<LookupKey, LookupRow[]>>>({});
  const [busy, setBusy] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, OverrideDraft>>(() =>
    Object.fromEntries((study.branch_overrides ?? []).map(({ branch_id, ...o }) => [branch_id, o])),
  );
  const rowsOf = (k: LookupKey): LookupRow[] => [...(lookups[k] as LookupRow[]), ...(extra[k] ?? [])];
  const setOverride = (branchId: string, field: OverrideField, v: string | null) =>
    setOverrides((o) => ({ ...o, [branchId]: { ...(o[branchId] ?? INHERIT), [field]: v } }));
  const added = (k: LookupKey) => (row: LookupRow) => setExtra((x) => ({ ...x, [k]: [...(x[k] ?? []), row] }));
  const set = <K extends keyof StudyUpsertPayload>(k: K, v: StudyUpsertPayload[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const deliveryOk = deliveryRuleValid(modeOf(rule), daysText);
  const nameOk = draft.name.trim().length > 0;

  async function save() {
    setBusy(true);
    try {
      const payload: StudyUpsertPayload = {
        ...draft,
        name: draft.name.trim(),
        indications: draft.indications?.trim() || null,
        delivery_days: rule.delivery_days,
        delivery_by_weekday: rule.delivery_by_weekday,
        branch_overrides: overridesPayload(overrides),
      };
      const saved = await run<"catalogue.study.upsert", StudyDetail>("catalogue.study.upsert", payload);
      toast.success(`${saved.clave} guardado (versión ${saved.version}). Las órdenes ya registradas conservan la versión anterior.`);
      onSaved(saved);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const lookup = (k: LookupKey, label: string, field: "area_id" | "sample_type_id" | "container_id" | "method_id" | "technique_id" | "equipment_id") => (
    <LookupSelect label={label} kind={k} rows={rowsOf(k)} value={draft[field]} onChange={(id) => set(field, id)} onCreated={added(k)} />
  );

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Field label="Nombre del estudio" error={nameOk ? null : "Escribe el nombre."}>
        <Input aria-label="Nombre del estudio" value={draft.name} onChange={(e) => set("name", e.target.value)} />
      </Field>
      <div className="grid-2">
        {lookup("areas", "Área", "area_id")}
        {lookup("sample_types", "Tipo de muestra", "sample_type_id")}
        {lookup("containers", "Recipiente", "container_id")}
        {lookup("methods", "Método", "method_id")}
        {lookup("techniques", "Técnica", "technique_id")}
        {lookup("equipment", "Equipo", "equipment_id")}
      </div>
      <DeliveryRuleField value={rule} onChange={setRule} daysText={daysText} onDaysText={setDaysText} />
      {branches.length > 0 && (
        <section className="stack" style={{ gap: 12 }} aria-label="Por sucursal">
          <div>
            <div className="kicker">Por sucursal</div>
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              Si una sucursal procesa este estudio con otro método o equipo, elígelo aquí. Las órdenes de esa sucursal imprimen ese método en el resultado.
            </p>
          </div>
          {branches.map((b) => {
            const o = overrides[b.id] ?? INHERIT;
            const equipment = rowsOf("equipment").filter((e) => {
              const eb = (lookups.equipment.find((x) => x.id === e.id) ?? null)?.branch_id ?? null;
              return eb === null || eb === b.id || e.id === o.equipment_id;
            });
            return (
              <fieldset key={b.id} className="stack" style={{ gap: 8, border: "1px solid var(--line)", borderRadius: 10, padding: 12, margin: 0 }}>
                <legend style={{ fontWeight: 600, padding: "0 6px" }}>{b.name}</legend>
                <div className="grid-3">
                  <OverrideSelect label="Método" branch={b} rows={rowsOf("methods")} value={o.method_id} inherited={draft.method_id} onChange={(v) => setOverride(b.id, "method_id", v)} />
                  <OverrideSelect label="Técnica" branch={b} rows={rowsOf("techniques")} value={o.technique_id} inherited={draft.technique_id} onChange={(v) => setOverride(b.id, "technique_id", v)} />
                  <OverrideSelect label="Equipo" branch={b} rows={equipment} value={o.equipment_id} inherited={draft.equipment_id} onChange={(v) => setOverride(b.id, "equipment_id", v)} />
                </div>
              </fieldset>
            );
          })}
        </section>
      )}
      <Field label="Indicaciones para el paciente" optional hint="Se imprimen en el ticket.">
        <Textarea aria-label="Indicaciones para el paciente" rows={3} value={draft.indications ?? ""} onChange={(e) => set("indications", e.target.value)} />
      </Field>
      <Switch checked={draft.is_active} onChange={(v) => set("is_active", v)}>
        Activo (se puede cotizar y registrar)
      </Switch>
      <p className="small muted">Al guardar se crea una versión nueva del estudio. Las órdenes ya registradas conservan la versión con la que se registraron.</p>
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" icon="check" busy={busy} disabled={!nameOk || !deliveryOk} onClick={() => void save()}>
          Guardar ficha
        </Button>
      </div>
    </div>
  );
}

// ---- Price -------------------------------------------------------------------------------------

/** "1,250.5" / "$1250.50" → 125050; null when it is not a non-negative amount with at most two decimals. */
export function parsePesos(input: string): number | null {
  const clean = input.replace(/[$,\s]/g, "");
  if (!/^\d{1,8}(\.\d{0,2})?$/.test(clean)) return null;
  return Math.round(Number(clean) * 100);
}

/** 125050 → "1,250.50" (es-MX grouping, two decimals, no currency sign: the field shows "$"). */
export function pesosText(cents: number): string {
  return (cents / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A price list the form may edit, with the study's current price on it. */
export interface PriceChoice {
  id: string;
  name: string;
  current: number | null;
}

/**
 * Edit one study's price on one list. With `choices` (more than one active list, pilot request of
 * 2026-11-02 "una lista para el Capulín y otra para SJ") the form shows which list it edits and
 * lets the Administrador switch; the amount field follows the chosen list.
 */
export function PriceForm({ title, priceListId, priceListName, studyId, current, choices, onSaved, onCancel }: { title: string; priceListId: string; priceListName: string; studyId: string; current: number | null; choices?: PriceChoice[]; onSaved: (cents: number) => void; onCancel: () => void }) {
  const toast = useToast();
  const [list, setList] = useState<PriceChoice>({ id: priceListId, name: priceListName, current });
  const [text, setText] = useState(current === null ? "" : pesosText(current));
  const [busy, setBusy] = useState(false);
  const cents = parsePesos(text);

  function pickList(id: string) {
    const next = choices?.find((c) => c.id === id);
    if (!next) return;
    setList(next);
    setText(next.current === null ? "" : pesosText(next.current));
  }

  async function save() {
    if (cents === null) return;
    setBusy(true);
    try {
      await run("catalogue.price.set", { price_list_id: list.id, study_id: studyId, package_id: null, amount_cents: cents });
      toast.success(`${title}: ${money(cents)} en ${list.name}.`);
      onSaved(cents);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      {choices && choices.length > 1 && (
        <Field label="Lista de precios">
          <Select value={list.id} onChange={(e) => pickList(e.target.value)} aria-label="Lista de precios">
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.current === null ? " · sin precio" : ` · ${money(c.current)}`}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field label={`Precio en ${list.name}`} hint={list.current === null ? "Sin precio en esta lista." : `Precio actual: ${money(list.current)}`} error={text.trim() && cents === null ? "Escribe el precio en pesos, con hasta dos decimales." : null}>
        <Input
          aria-label="Precio en pesos"
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => cents !== null && setText(pesosText(cents))}
          onKeyDown={(e) => e.key === "Enter" && void save()}
          autoFocus
        />
      </Field>
      <p className="small muted">El precio nuevo aplica a las órdenes y cotizaciones que se registren a partir de ahora. Las ya registradas conservan su precio.</p>
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" icon="check" busy={busy} disabled={cents === null || cents === list.current} onClick={() => void save()}>
          Guardar precio
        </Button>
      </div>
    </div>
  );
}

// ---- Analytes ----------------------------------------------------------------------------------


export interface IntervalDraft {
  key: string;
  /** null = a general row; a branch id = that branch's own set (ADR 0020). */
  branchId: string | null;
  sex: "" | "M" | "F";
  ageMin: string;
  ageMax: string;
  ageUnit: AgeBandUnit;
  /** The days the row was loaded with, kept while its age fields are not edited (`boundDays`). */
  stored?: { unit: AgeBandUnit; min: number | null; max: number | null; minText: string; maxText: string };
  condition: string;
  low: string;
  high: string;
  expected: string;
  display: string;
  /** Once the chemist edits the printed text by hand, changing the range no longer clears it. */
  displayTouched: boolean;
}

export interface AnalyteDraft {
  key: string;
  analyte_id: string | null;
  code: string;
  name: string;
  kind: AnalyteKind;
  unit: string;
  decimals: string;
  /** The answers, one per row of the editor, in capture order; the first is «por defecto». */
  options: string[];
  formula: string | null;
  /**
   * Per-branch formulas (ADR 0032): branch id -> its own formula ("" while being written) or null =
   * captured at that branch. A branch not in the record inherits the general formula.
   */
  branchFormulas: Record<string, string | null>;
  section: string | null;
  intervals: IntervalDraft[];
}

let keySeq = 0;
const nextKey = () => `k${++keySeq}`;

/** Days → the largest unit that divides both ends exactly (años, meses, días). */
export function ageUnitFor(min: number | null, max: number | null): AgeBandUnit {
  const vals = [min, max].filter((v): v is number => v !== null && v > 0);
  if (vals.length && vals.every((v) => daysToAgeValue(v, "a") !== null)) return "a";
  if (vals.length && vals.every((v) => daysToAgeValue(v, "m") !== null)) return "m";
  return vals.length ? "d" : "a";
}

const numText = (n: number | null) => (n === null ? "" : String(n));

export function toDrafts(study: StudyDetail): AnalyteDraft[] {
  return study.analytes
    .filter((a) => a.is_active)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((a) => analyteDraft(a));
}

/** One analyte as an editor draft. `asNew` drops its id (a copy picked from another study). */
export function analyteDraft(a: Analyte & { reference_intervals: StudyDetail["analytes"][number]["reference_intervals"] }, asNew = false): AnalyteDraft {
  return {
    key: nextKey(),
    analyte_id: asNew ? null : a.id,
    code: a.code,
    name: a.name,
    kind: a.kind,
    unit: a.unit ?? "",
    decimals: a.decimals === null ? "" : String(a.decimals),
    options: [...(a.options ?? [])],
    formula: a.formula,
    branchFormulas: Object.fromEntries((a.branch_formulas ?? []).map((b) => [b.branch_id, b.formula])),
    section: asNew ? null : a.section,
    intervals: a.reference_intervals
      .slice()
      .sort((x, y) => x.sort_order - y.sort_order)
      .map((r) => {
        const unit = ageUnitFor(r.age_min_days, r.age_max_days);
        const ageMin = r.age_min_days === null ? "" : String(daysToAgeValue(r.age_min_days, unit));
        const ageMax = r.age_max_days === null ? "" : String(daysToAgeValue(r.age_max_days, unit));
        return {
          key: nextKey(),
          branchId: r.branch_id ?? null,
          sex: r.sex ?? "",
          ageMin,
          ageMax,
          ageUnit: unit,
          stored: { unit, min: r.age_min_days, max: r.age_max_days, minText: ageMin, maxText: ageMax },
          condition: r.condition ?? "",
          low: numText(r.low),
          high: numText(r.high),
          expected: r.expected_text ?? "",
          display: r.display_text ?? "",
          displayTouched: false,
        };
      }),
  };
}

/** Case- and accent-insensitive key of an answer, so "Ámbar" and "ambar" are one answer. */
export const answerKey = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

/** The answers as saved: trimmed, empty rows dropped, repeats dropped (first spelling wins). */
export function cleanOptions(list: readonly string[]): string[] {
  const seen = new Set<string>();
  return list.map((o) => o.trim()).filter((o) => o !== "" && !seen.has(answerKey(o)) && (seen.add(answerKey(o)), true));
}

/** The first answer written twice (accents and capitals ignored), or null. */
export function repeatedOption(list: readonly string[]): string | null {
  const seen = new Set<string>();
  for (const o of list.map((x) => x.trim()).filter(Boolean)) {
    if (seen.has(answerKey(o))) return o;
    seen.add(answerKey(o));
  }
  return null;
}

/** "1,500.5" → 1500.5; "" → null; NaN when it is not a number. */
function num(s: string): number | null {
  const t = s.replace(/[,\s]/g, "");
  if (t === "") return null;
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : NaN;
}

function finite(s: string): number | null {
  const n = num(s);
  return n === null || Number.isNaN(n) ? null : n;
}

function ageDays(s: string, unit: AgeBandUnit): number | null {
  const n = num(s);
  if (n === null) return null;
  return Number.isFinite(n) && n >= 0 ? ageToDays(n, unit) : NaN;
}

/**
 * A bound in days. A bound the chemist did not touch keeps the days it was stored with (a band
 * saved when a year was 365 days stays 6570 until she edits it), so opening and saving a study
 * never moves a reference by itself.
 */
function boundDays(r: IntervalDraft, which: "min" | "max"): number | null {
  const s = r.stored;
  const text = which === "min" ? r.ageMin : r.ageMax;
  if (s && s.unit === r.ageUnit && text === (which === "min" ? s.minText : s.maxText)) return which === "min" ? s.min : s.max;
  return ageDays(text, r.ageUnit);
}

/** Problems that would make the save fail or the report wrong, keyed by draft key. */
export function validateDrafts(drafts: AnalyteDraft[]): Map<string, string> {
  const out = new Map<string, string>();
  const fps = formulaProblems(drafts);
  const seen = new Map<string, number>();
  for (const a of drafts) seen.set(a.code.trim(), (seen.get(a.code.trim()) ?? 0) + 1);
  for (const a of drafts) {
    const code = a.code.trim();
    if (!a.name.trim()) out.set(a.key, "Escribe el nombre del analito.");
    else if (!code) out.set(a.key, "Escribe la clave del analito.");
    else if ((seen.get(code) ?? 0) > 1) out.set(a.key, `La clave ${code} está repetida.`);
    else if (a.decimals.trim() !== "" && !/^[0-6]$/.test(a.decimals.trim())) out.set(a.key, "Los decimales van de 0 a 6.");
    else if (a.kind === "reference" && !a.options.some((o) => o.trim())) out.set(a.key, "Escribe al menos una respuesta.");
    else if ((a.kind === "reference" || a.kind === "text") && repeatedOption(a.options)) out.set(a.key, `La respuesta «${repeatedOption(a.options)}» está repetida.`);
    const fp = fps.get(a.key);
    if (fp) out.set(`${a.key}:formula`, fp.message);
    for (const r of a.intervals) {
      const [lo, hi, amin, amax] = [num(r.low), num(r.high), boundDays(r, "min"), boundDays(r, "max")];
      if ([lo, hi, amin, amax].some((v) => v !== null && Number.isNaN(v))) out.set(r.key, "Revisa los números de esta fila.");
      else if (lo !== null && hi !== null && lo > hi) out.set(r.key, "El mínimo es mayor que el máximo.");
      else if (amin !== null && amax !== null && amin > amax) out.set(r.key, "La edad mínima es mayor que la máxima.");
      // An empty row of a branch would hide the general values there and print nothing.
      else if (r.branchId !== null && lo === null && hi === null && !r.expected.trim() && !r.display.trim()) out.set(r.key, "Escribe el rango o el texto de este valor, o quítalo.");
    }
  }
  return out;
}

/** A draft's per-branch formulas as the payload and the checker take them; only a numeric analyte has any. */
export function branchFormulaList(a: AnalyteDraft): { branch_id: string; formula: string | null }[] {
  if (a.kind !== "numeric") return [];
  return Object.entries(a.branchFormulas).map(([branch_id, formula]) => ({ branch_id, formula: formula === null ? null : formula.trim() }));
}

/**
 * The study as the formula checker sees it (ADR 0023): only numeric analytes keep a formula, with
 * their per-branch formulas (ADR 0032) so every branch's effective set is checked.
 */
export function formulaStudy(drafts: AnalyteDraft[]): BranchFormulaAnalyte[] {
  return drafts.map((a) => ({
    id: a.key,
    code: a.code.trim(),
    kind: a.kind,
    decimals: a.decimals.trim() === "" ? null : Number(a.decimals),
    formula: a.kind === "numeric" ? a.formula : null,
    branch_formulas: branchFormulaList(a),
  }));
}

/** The draft's own empty fields: a general or branch formula opened but not written. */
export function emptyFormulaProblem(a: AnalyteDraft): BranchFormulaProblem | null {
  if (a.kind !== "numeric") return null;
  if (a.formula !== null && a.formula.trim() === "") return { message: "Escribe la fórmula o quítala.", position: 0, branch_id: null };
  const blank = Object.entries(a.branchFormulas).find(([, f]) => f !== null && f.trim() === "");
  return blank ? { message: "Escribe la fórmula de esta sucursal o elige otra opción.", position: 0, branch_id: blank[0] } : null;
}

/** Formula problems by draft key: the server's own check on every branch's set, plus an open but empty field. */
export function formulaProblems(drafts: AnalyteDraft[]): Map<string, BranchFormulaProblem> {
  const out = checkStudyFormulasByBranch(formulaStudy(drafts));
  for (const a of drafts) {
    const empty = emptyFormulaProblem(a);
    if (empty) out.set(a.key, empty);
  }
  return out;
}

export function draftsPayload(drafts: AnalyteDraft[]): NonNullable<StudyUpsertPayload["analytes"]> {
  return drafts.map((a, i) => {
    const hasValue = a.kind !== "subtitle";
    return {
      analyte_id: a.analyte_id,
      code: a.code.trim(),
      name: a.name.trim(),
      kind: a.kind,
      unit: hasValue && a.unit.trim() ? a.unit.trim() : null,
      decimals: a.kind === "numeric" && a.decimals.trim() !== "" ? Number(a.decimals.trim()) : null,
      // Open (text) analytes keep their suggested answers too: the save used to drop them.
      options: a.kind === "reference" || (a.kind === "text" && cleanOptions(a.options).length) ? cleanOptions(a.options) : null,
      formula: a.kind === "numeric" && a.formula?.trim() ? a.formula.trim() : null,
      branch_formulas: branchFormulaList(a),
      section: a.section,
      sort_order: i,
      reference_intervals: hasValue
        ? a.intervals.map((r, j) => ({
            branch_id: r.branchId,
            sex: r.sex || null,
            age_min_days: boundDays(r, "min"),
            age_max_days: boundDays(r, "max"),
            condition: r.condition.trim() || null,
            low: num(r.low),
            high: num(r.high),
            expected_text: r.expected.trim() || null,
            display_text: r.display.trim() || null,
            sort_order: j,
          }))
        : [],
    };
  });
}

const KIND_LABEL: Record<AnalyteKind, string> = { numeric: "Numérico", text: "Texto", reference: "Opciones", subtitle: "Subtítulo", document: "Documento" };

function emptyInterval(branchId: string | null = null): IntervalDraft {
  return { key: nextKey(), branchId, sex: "", ageMin: "", ageMax: "", ageUnit: "a", condition: "", low: "", high: "", expected: "", display: "", displayTouched: false };
}

/** A blank analyte for a creation form («Nuevo analito»): numeric by default, one general reference row. */
export function blankAnalyte(kind: AnalyteKind = "numeric"): AnalyteDraft {
  return { key: nextKey(), analyte_id: null, code: "", name: "", kind, unit: "", decimals: kind === "numeric" ? "0" : "", options: [], formula: null, branchFormulas: {}, section: null, intervals: [emptyInterval()] };
}

export function AnalytesEditor({ study, branches = [], onSaved, onCancel }: { study: StudyDetail; branches?: Branch[]; onSaved: (s: StudyDetail) => void; onCancel: () => void }) {
  const toast = useToast();
  const [drafts, setDrafts] = useState<AnalyteDraft[]>(() => toDrafts(study));
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const problems = useMemo(() => validateDrafts(drafts), [drafts]);
  const fStudy = useMemo(() => formulaStudy(drafts), [drafts]);
  const fProblems = useMemo(() => formulaProblems(drafts), [drafts]);
  // «Quitar» never leaves a study without analytes (the server refuses it too): to retire the study, «Quitar del catálogo».
  const emptied = study.analytes.some((a) => a.is_active && a.kind !== "subtitle") && !drafts.some((d) => d.kind !== "subtitle");

  const patch = (key: string, p: Partial<AnalyteDraft>) => setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...p } : d)));
  const patchInterval = (key: string, rk: string, p: Partial<IntervalDraft>) =>
    setDrafts((ds) =>
      ds.map((d) =>
        d.key !== key
          ? d
          : {
              ...d,
              intervals: d.intervals.map((r) => {
                if (r.key !== rk) return r;
                const next = { ...r, ...p };
                // The report prints the stored text verbatim; a changed range would print the old
                // text, so unless the chemist wrote that text by hand it is regenerated from the range.
                if (!next.displayTouched && ("low" in p || "high" in p || "expected" in p)) next.display = "";
                return next;
              }),
            },
      ),
    );
  // Drag by the handle as in Catálogo → Áreas; the arrows stay as the keyboard path.
  const { dragId, rowRef, gripProps } = useDragReorder((upd) =>
    setDrafts((ds) => {
      const cur = ds.map((d) => d.key);
      const next = typeof upd === "function" ? upd(cur) : upd;
      return next.join() === cur.join() ? ds : next.map((k) => ds.find((d) => d.key === k)!);
    }),
  );
  const move = (i: number, by: number) =>
    setDrafts((ds) => {
      const j = i + by;
      if (j < 0 || j >= ds.length) return ds;
      const out = ds.slice();
      [out[i], out[j]] = [out[j]!, out[i]!];
      return out;
    });
  const add = (kind: AnalyteKind) =>
    setDrafts((ds) => [
      ...ds,
      { key: nextKey(), analyte_id: null, code: kind === "subtitle" ? `SUB${ds.filter((d) => d.kind === "subtitle").length + 1}` : "", name: "", kind, unit: "", decimals: kind === "numeric" ? "0" : "", options: [], formula: null, branchFormulas: {}, section: null, intervals: kind === "subtitle" ? [] : [emptyInterval()] },
    ]);

  async function save() {
    setBusy(true);
    try {
      const saved = await run<"catalogue.study.upsert", StudyDetail>("catalogue.study.upsert", { ...fichaPayload(study), analytes: draftsPayload(drafts) });
      toast.success(`${saved.clave}: analitos guardados (versión ${saved.version}). Las órdenes ya registradas conservan la versión anterior.`);
      onSaved(saved);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <p className="small muted">
        El orden de esta lista es el orden del informe. Al quitar un analito deja de aparecer en las órdenes nuevas; sus resultados anteriores se conservan. Al guardar se crea una versión nueva del estudio y las órdenes ya registradas conservan la versión anterior.
      </p>
      {drafts.length === 0 && <div className="faint">Este estudio no tiene analitos. Agrega el primero.</div>}
      {emptied && <Alert tone="amber">Un estudio necesita al menos un analito. Agrega otro antes de guardar, o retira el estudio con «Quitar del catálogo».</Alert>}
      {drafts.map((a, i) => {
        const err = problems.get(a.key);
        return (
          <section key={a.key} ref={rowRef(a.key)} className={`card${dragId === a.key ? " dragging" : ""}`} style={{ padding: 14 }} aria-label={`Analito ${i + 1}`}>
            <div className="stack" style={{ gap: 10 }}>
              <div className="row between">
                <strong className="small muted row" style={{ gap: 8 }}>
                  <span className="drag-handle" aria-hidden="true" title="Arrastrar" {...gripProps(a.key)}>
                    <Icon name="grip" />
                  </span>
                  <span>
                    {i + 1}. {KIND_LABEL[a.kind]}
                    {a.analyte_id === null ? " · nuevo" : ""}
                  </span>
                </strong>
                <span className="row" style={{ gap: 2 }}>
                  <IconButton size="sm" icon="chevronLeft" label={`Subir ${a.name || "analito"}`} style={{ transform: "rotate(90deg)" }} disabled={i === 0} onClick={() => move(i, -1)} />
                  <IconButton size="sm" icon="chevronDown" label={`Bajar ${a.name || "analito"}`} disabled={i === drafts.length - 1} onClick={() => move(i, 1)} />
                  <IconButton size="sm" icon="x" label={`Quitar ${a.name || "analito"}`} onClick={() => setDrafts((ds) => ds.filter((d) => d.key !== a.key))} />
                </span>
              </div>
              <AnalyteFields
                analyte={a}
                branches={branches}
                problems={problems}
                study={fStudy}
                formulaProblem={fProblems.get(a.key) ?? null}
                onPatch={(p) => patch(a.key, p)}
                onPatchInterval={(rk, p) => patchInterval(a.key, rk, p)}
              />
              {err && <Alert tone="red">{err}</Alert>}
            </div>
          </section>
        );
      })}
      <div className="row wrap" style={{ gap: 8 }}>
        <Button size="sm" icon="plus" onClick={() => add("numeric")}>
          Analito numérico
        </Button>
        <Button size="sm" icon="plus" onClick={() => add("reference")}>
          Analito con opciones
        </Button>
        <Button size="sm" icon="plus" onClick={() => add("text")}>
          Analito de texto
        </Button>
        <Button size="sm" icon="plus" onClick={() => add("subtitle")}>
          Subtítulo
        </Button>
        <Button size="sm" icon="search" onClick={() => setPicking(true)}>
          Analito de otro estudio
        </Button>
      </div>
      {picking && (
        <AnalytePicker
          exclude={drafts.map((d) => d.code.trim())}
          onPick={(row) => {
            setDrafts((ds) => [...ds, analyteDraft(row, true)]);
            setPicking(false);
          }}
          onCancel={() => setPicking(false)}
        />
      )}
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" icon="check" busy={busy} disabled={problems.size > 0 || emptied} onClick={() => void save()}>
          Guardar analitos
        </Button>
      </div>
    </div>
  );
}

/**
 * The fields of one analyte: name, clave, result type, unit, decimals, answers, formula and
 * reference values. Shared by the study's analytes editor and Catálogo → Analitos, so both edit
 * an analyte with the same controls. `codeLocked` shows the clave read-only (Analitos: the clave
 * is what links the studies); `kinds` limits the result types offered.
 */
export function AnalyteFields({
  analyte: a,
  branches,
  problems,
  study,
  formulaProblem,
  codeLocked = false,
  kinds = Object.keys(KIND_LABEL) as AnalyteKind[],
  onPatch,
  onPatchInterval,
}: {
  analyte: AnalyteDraft;
  branches: Branch[];
  problems: Map<string, string>;
  /** The study as edited, with every analyte's per-branch formulas (ADR 0032). */
  study: BranchFormulaAnalyte[];
  /** A problem with `branch_id` belongs to that branch's formula (ADR 0032); without, to the general one. */
  formulaProblem: FormulaProblem | BranchFormulaProblem | null;
  codeLocked?: boolean;
  kinds?: AnalyteKind[];
  onPatch: (p: Partial<AnalyteDraft>) => void;
  onPatchInterval: (rk: string, p: Partial<IntervalDraft>) => void;
}) {
  return (
    <>
      <div className="grid-4">
        <Field label="Nombre">
          <Input aria-label="Nombre del analito" value={a.name} onChange={(e) => onPatch({ name: e.target.value })} />
        </Field>
        <Field label="Clave">
          <Input aria-label="Clave del analito" className="mono" value={a.code} readOnly={codeLocked} disabled={codeLocked} onChange={(e) => onPatch({ code: e.target.value })} />
        </Field>
        <Field label="Tipo de resultado">
          <Select aria-label="Tipo de analito" value={a.kind} onChange={(e) => onPatch({ kind: e.target.value as AnalyteKind })}>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </Select>
        </Field>
        {a.kind !== "subtitle" && (
          <Field label="Unidad" optional hint={a.kind === "document" ? "En un cultivo se muestra junto a la cuenta y se guarda con el número. Por ejemplo: UFC/mL." : undefined}>
            <Input aria-label="Unidad" value={a.unit} onChange={(e) => onPatch({ unit: e.target.value })} />
          </Field>
        )}
        {a.kind === "numeric" && (
          <Field label="Decimales">
            <Input aria-label="Decimales" inputMode="numeric" value={a.decimals} onChange={(e) => onPatch({ decimals: e.target.value })} />
          </Field>
        )}
      </div>
      {(a.kind === "reference" || a.kind === "text") && <OptionsEditor analyte={a} onPatch={onPatch} />}
      {a.kind === "numeric" && (
        <FormulaField
          code={a.code.trim()}
          formula={a.formula}
          decimals={a.decimals.trim() === "" ? null : Number(a.decimals)}
          study={studyAtBranch(study, null)}
          problem={branchOf(formulaProblem) === null ? formulaProblem : null}
          onChange={(formula) => onPatch({ formula })}
        />
      )}
      {a.kind === "numeric" && (branches.length > 1 || Object.keys(a.branchFormulas).length > 0) && (
        <BranchFormulasEditor
          analyte={a}
          branches={branches}
          study={study}
          problem={branchOf(formulaProblem) !== null ? (formulaProblem as BranchFormulaProblem) : null}
          onChange={(branchFormulas) => onPatch({ branchFormulas })}
        />
      )}
      {a.kind !== "subtitle" && a.kind !== "document" && (
        <IntervalsEditor analyte={a} branches={branches} problems={problems} onPatch={onPatchInterval} onChange={(intervals) => onPatch({ intervals })} />
      )}
    </>
  );
}

/** The branch a formula problem belongs to: null = the general formula (ADR 0032). */
const branchOf = (p: FormulaProblem | BranchFormulaProblem | null): string | null => (p && "branch_id" in p ? p.branch_id : null);

type BranchFormulaMode = "inherit" | "own" | "captured";

const MODE_OPTIONS: { value: BranchFormulaMode; label: string }[] = [
  { value: "inherit", label: "Igual que la general" },
  { value: "own", label: "Fórmula propia" },
  { value: "captured", label: "Se captura" },
];

/** What a branch does with the analyte, as one plain sentence (ADR 0032). */
export function branchFormulaSentence(branchName: string, own: string | null | undefined, general: string | null): string {
  if (own === undefined) return general ? `En ${branchName}: igual que la fórmula general (= ${general})` : `En ${branchName}: se captura, igual que en general`;
  if (own === null) return `En ${branchName}: se captura (lo da el equipo)`;
  return own.trim() ? `En ${branchName}: se calcula = ${own.trim()}` : `En ${branchName}: fórmula propia, falta escribirla`;
}

/**
 * «Por sucursal» (ADR 0032, pilot answer of 2026-11-03: one branch's analyser gives urea and BUN
 * is calculated, the other's gives BUN and urea is calculated). One plain sentence per branch —
 * «En Sucursal San José Iturbide: se captura (lo da el equipo)», «En Matriz Villa El Capulín: se
 * calcula = URE / 2.1428», «…: igual que la fórmula general» — and a «Cambiar» that opens the
 * three choices for that branch only. Lab staff read what happens; they do not have to decode a
 * three-state control. A branch with a problem opens by itself. A branch this user cannot see
 * stays listed.
 */
export function BranchFormulasEditor({
  analyte: a,
  branches,
  study,
  problem,
  onChange,
}: {
  analyte: AnalyteDraft;
  branches: Branch[];
  study: BranchFormulaAnalyte[];
  problem: BranchFormulaProblem | null;
  onChange: (branchFormulas: Record<string, string | null>) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const own = Object.keys(a.branchFormulas);
  const general = a.formula?.trim() ? a.formula.trim() : null;
  const rows = [...branches.map((b) => ({ id: b.id, name: b.name })), ...own.filter((id) => !branches.some((b) => b.id === id)).map((id) => ({ id, name: "otra sucursal" }))];
  const decimals = a.decimals.trim() === "" ? null : Number(a.decimals);
  const setMode = (branchId: string, mode: BranchFormulaMode) => {
    const next = { ...a.branchFormulas };
    if (mode === "inherit") delete next[branchId];
    else next[branchId] = mode === "captured" ? null : (a.branchFormulas[branchId] ?? "");
    onChange(next);
  };
  return (
    <section className="stack" style={{ gap: 8 }} aria-label="Por sucursal">
      <div className="kicker">Por sucursal</div>
      {rows.map((b) => {
        const v = a.branchFormulas[b.id];
        const mode: BranchFormulaMode = v === undefined ? "inherit" : v === null ? "captured" : "own";
        const p = problem && problem.branch_id === b.id ? problem : null;
        const open = editing === b.id || p !== null;
        return (
          <div key={b.id} className="stack" style={{ gap: 6 }}>
            <div className="row between wrap" style={{ gap: 8, minHeight: 44 }}>
              <span data-branch-formula={b.id}>{branchFormulaSentence(b.name, v, general)}</span>
              {!open && (
                <Button size="sm" variant="ghost" aria-label={`Cambiar la fórmula en ${b.name}`} onClick={() => setEditing(b.id)}>
                  Cambiar
                </Button>
              )}
            </div>
            {open && (
              <div className="stack" style={{ gap: 8, paddingLeft: 10, borderLeft: "3px solid var(--brand-tint)" }}>
                <div role="group" aria-label={`Fórmula en ${b.name}`}>
                  <Segmented small value={mode} options={MODE_OPTIONS} onChange={(m) => setMode(b.id, m)} />
                </div>
                {mode === "own" && (
                  <FormulaField
                    code={a.code.trim()}
                    formula={v ?? ""}
                    decimals={decimals}
                    study={studyAtBranch(study, b.id)}
                    problem={p}
                    where={b.name}
                    removable={false}
                    onChange={(f) => onChange({ ...a.branchFormulas, [b.id]: f ?? "" })}
                  />
                )}
                {mode !== "own" && p && (
                  <span className="small" role="alert" style={{ color: "var(--red)" }}>
                    {p.message}
                  </span>
                )}
                {p === null && (
                  <div>
                    <Button size="sm" variant="ghost" icon="check" onClick={() => setEditing(null)}>
                      Listo
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

/**
 * The answers of a text analyte (pilot request of 2026-11-03: "en los EGO, en las células o
 * cristales, agregamos algo"), as the previous system had them: «Restringido» = only an answer of
 * the list is accepted (kind `reference`, the server refuses anything else); «Abierto» = the list
 * is offered and anything else may be typed (kind `text`). One row per answer, so adding, renaming,
 * removing and reordering are each one tap; the first answer is the one «Llenar normales» writes
 * in Captura, the previous system's «Resultado por defecto». A field can always be left empty at
 * Captura, so there is no "opción vacía" to enable.
 */
export function OptionsEditor({ analyte: a, onPatch }: { analyte: AnalyteDraft; onPatch: (p: Partial<AnalyteDraft>) => void }) {
  const [adding, setAdding] = useState("");
  const options = a.options;
  // Stable row ids so a dragged row keeps its identity while the list reorders (answers can repeat or be empty).
  const seq = useRef(0);
  const ids = useRef<string[]>([]);
  while (ids.current.length < options.length) ids.current.push(`o${seq.current++}`);
  if (ids.current.length > options.length) ids.current.length = options.length;
  const set = (next: string[]) => onPatch({ options: next });
  const move = (i: number, j: number) => {
    if (j < 0 || j >= options.length) return;
    ids.current = moveItem(ids.current, i, j);
    set(moveItem(options, i, j));
  };
  // Drag by the handle (stylus, finger, mouse) as in Catálogo → Áreas; the arrows stay as the keyboard path.
  const { dragId, rowRef, gripProps } = useDragReorder((upd) => {
    const cur = ids.current.slice();
    const next = typeof upd === "function" ? upd(cur) : upd;
    if (next.join() === cur.join()) return;
    const byId = new Map(cur.map((id, i) => [id, options[i]!]));
    ids.current = next;
    set(next.map((id) => byId.get(id)!));
  });
  const add = () => {
    // A pasted list (one per line, or the previous system's comma list) adds every answer.
    const incoming = adding.includes("\n") ? adding.split("\n") : adding.includes(",") && adding.split(",").length > 2 ? adding.split(",") : [adding];
    const have = new Set(options.map(answerKey));
    const fresh = incoming.map((o) => o.trim()).filter((o) => o && !have.has(answerKey(o)) && (have.add(answerKey(o)), true));
    if (fresh.length) set([...options, ...fresh]);
    setAdding("");
  };
  const restricted = a.kind === "reference";
  return (
    <section className="stack" style={{ gap: 10 }} aria-label="Respuestas">
      <div className="row between wrap" style={{ gap: 8 }}>
        <strong className="small">Respuestas</strong>
        <Segmented
          small
          value={restricted ? "reference" : "text"}
          onChange={(k) => onPatch({ kind: k })}
          options={[
            { value: "reference", label: "Restringido" },
            { value: "text", label: "Abierto" },
          ]}
        />
      </div>
      <span className="small muted">
        {restricted ? "En Captura solo se puede elegir una respuesta de la lista." : "En Captura la lista se ofrece como sugerencia y también se puede escribir otra respuesta."} La primera es la que pone «Llenar normales».
      </span>
      {options.length === 0 && <span className="small faint">{restricted ? "Agrega al menos una respuesta." : "Sin respuestas sugeridas: en Captura se escribe libre."}</span>}
      <ol className="stack" style={{ gap: 6, listStyle: "none", margin: 0, padding: 0 }}>
        {options.map((o, i) => (
          <li key={ids.current[i]} ref={rowRef(ids.current[i]!)} className={`row${dragId === ids.current[i] ? " dragging" : ""}`} style={{ gap: 6 }}>
            <span className="drag-handle" aria-hidden="true" title="Arrastrar" {...gripProps(ids.current[i]!)}>
              <Icon name="grip" />
            </span>
            <span className="small faint tnum" style={{ width: 22, textAlign: "right" }}>
              {i + 1}.
            </span>
            <Input aria-label={`Respuesta ${i + 1}`} value={o} onChange={(e) => set(options.map((x, j) => (j === i ? e.target.value : x)))} style={{ flex: 1, minWidth: 0 }} />
            {i === 0 ? (
              <Chip tone="green">Por defecto</Chip>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => move(i, 0)} aria-label={`Poner ${o || "respuesta"} por defecto`}>
                Por defecto
              </Button>
            )}
            <IconButton size="sm" icon="chevronLeft" label={`Subir ${o || "respuesta"}`} style={{ transform: "rotate(90deg)" }} disabled={i === 0} onClick={() => move(i, i - 1)} />
            <IconButton size="sm" icon="chevronDown" label={`Bajar ${o || "respuesta"}`} disabled={i === options.length - 1} onClick={() => move(i, i + 1)} />
            <IconButton size="sm" icon="x" label={`Quitar ${o || "respuesta"}`} onClick={() => { ids.current = ids.current.filter((_, j) => j !== i); set(options.filter((_, j) => j !== i)); }} />
          </li>
        ))}
      </ol>
      <div className="row" style={{ gap: 8 }}>
        <Input
          aria-label="Nueva respuesta"
          placeholder="Nueva respuesta"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          style={{ flex: 1, minWidth: 0 }}
        />
        <Button size="sm" icon="plus" disabled={!adding.trim()} onClick={add}>
          Agregar respuesta
        </Button>
      </div>
    </section>
  );
}

/**
 * The analyte's reference values, general first, then one block per branch that has its own
 * (ADR 0020): at that branch its own rows replace the general ones; every other branch uses the
 * general rows. Sex, age and condition choose within a block, as before.
 */
export function IntervalsEditor({ analyte, branches, problems, onPatch, onChange }: { analyte: AnalyteDraft; branches: Branch[]; problems: Map<string, string>; onPatch: (rk: string, p: Partial<IntervalDraft>) => void; onChange: (rows: IntervalDraft[]) => void }) {
  const general = analyte.intervals.filter((r) => r.branchId === null);
  const own = branches.filter((b) => analyte.intervals.some((r) => r.branchId === b.id));
  // Rows of a branch this user cannot see (or one no longer listed) are kept and shown by id.
  const unknown = [...new Set(analyte.intervals.map((r) => r.branchId).filter((id): id is string => id !== null && !branches.some((b) => b.id === id)))];
  const without = branches.filter((b) => !own.includes(b));
  const [adding, setAdding] = useState("");
  const names = (list: { name: string }[]) => list.map((b) => b.name).join(" y ");

  const addRow = (branchId: string | null) => {
    // A new row goes at the end of its own block.
    const last = analyte.intervals.map((r) => r.branchId).lastIndexOf(branchId);
    const rows = analyte.intervals.slice();
    rows.splice(last < 0 ? rows.length : last + 1, 0, emptyInterval(branchId));
    onChange(rows);
  };

  const block = (branchId: string | null, title: string, hint: string, rows: IntervalDraft[]) => (
    <section className={`interval-block${branchId ? " branch" : ""}`} aria-label={title}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <div className="stack" style={{ gap: 2 }}>
          <strong className="small">{title}</strong>
          <span className="small muted">{hint}</span>
        </div>
        {branchId !== null && (
          <Button size="sm" variant="ghost" icon="x" onClick={() => onChange(analyte.intervals.filter((r) => r.branchId !== branchId))}>
            Quitar los valores de esta sucursal
          </Button>
        )}
      </div>
      {rows.length === 0 && <span className="small faint">Sin valores de referencia.</span>}
      {rows.map((r) => (
        <IntervalRow key={r.key} analyte={analyte} row={r} error={problems.get(r.key)} onPatch={onPatch} onRemove={() => onChange(analyte.intervals.filter((x) => x.key !== r.key))} />
      ))}
      <div>
        <Button size="sm" variant="ghost" icon="plus" onClick={() => addRow(branchId)}>
          Agregar valor de referencia
        </Button>
      </div>
    </section>
  );

  const generalHint =
    own.length === 0
      ? branches.length > 1
        ? "Se usan en todas las sucursales."
        : "Se usan en el informe de este analito."
      : without.length > 0
        ? `Se usan en ${names(without)}. ${names(own)} ${own.length === 1 ? "tiene" : "tienen"} sus propios valores.`
        : "Ninguna sucursal los usa: todas tienen sus propios valores.";

  return (
    <div className="stack" style={{ gap: 10 }}>
      <strong className="small">Valores de referencia</strong>
      {block(null, own.length > 0 || unknown.length > 0 ? "Valores generales" : "Valores de referencia", generalHint, general)}
      {own.map((b) =>
        block(b.id, `Valores para ${b.name}`, `Sustituyen a los generales en ${b.name}. Las demás sucursales usan los generales.`, analyte.intervals.filter((r) => r.branchId === b.id)),
      )}
      {unknown.map((id) => block(id, "Valores para otra sucursal", "Sustituyen a los generales en esa sucursal.", analyte.intervals.filter((r) => r.branchId === id)))}
      {branches.length > 1 && without.length > 0 && (
        <div className="row wrap" style={{ gap: 8, alignItems: "flex-end" }}>
          <Field label="Valores distintos en una sucursal" optional hint="Cuando esa sucursal usa otro método y sus valores de referencia son otros.">
            <Select aria-label="Sucursal con valores propios" value={adding} onChange={(e) => setAdding(e.target.value)} style={{ width: 260 }}>
              <option value="">Elige la sucursal</option>
              {without.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            size="sm"
            icon="plus"
            disabled={!adding}
            onClick={() => {
              addRow(adding);
              setAdding("");
            }}
          >
            Agregar valores para esta sucursal
          </Button>
        </div>
      )}
    </div>
  );
}

function IntervalRow({ analyte, row: r, error, onPatch, onRemove }: { analyte: AnalyteDraft; row: IntervalDraft; error: string | undefined; onPatch: (rk: string, p: Partial<IntervalDraft>) => void; onRemove: () => void }) {
  // Text analytes take a range too: the previous system typed most quantitative analytes as text,
  // and a value that is a plain number is judged against it (ADR 0020).
  const bounds = analyte.kind === "numeric" || analyte.kind === "text";
  const preview = intervalDisplay(
    { id: "", sex: null, age_min_days: null, age_max_days: null, condition: null, low: finite(r.low), high: finite(r.high), expected_text: r.expected.trim() || null, display_text: r.display.trim() || null },
    analyte.unit.trim() || null,
  );
  return (
    <div className="stack" style={{ gap: 6, paddingLeft: 10, borderLeft: "3px solid var(--line-soft)" }} aria-label="Valor de referencia">
      <div className="row wrap" style={{ gap: 8, alignItems: "flex-end" }}>
        <Field label="Sexo">
          <Select aria-label="Sexo" value={r.sex} onChange={(e) => onPatch(r.key, { sex: e.target.value as IntervalDraft["sex"] })} style={{ width: 130 }}>
            <option value="">Ambos</option>
            <option value="F">Mujeres</option>
            <option value="M">Hombres</option>
          </Select>
        </Field>
        <Field label="Edad desde">
          <Input aria-label="Edad desde" inputMode="decimal" value={r.ageMin} onChange={(e) => onPatch(r.key, { ageMin: e.target.value })} style={{ width: 80 }} />
        </Field>
        <Field label="hasta">
          <Input aria-label="Edad hasta" inputMode="decimal" value={r.ageMax} onChange={(e) => onPatch(r.key, { ageMax: e.target.value })} style={{ width: 80 }} />
        </Field>
        <Field label="en">
          <Select aria-label="Unidad de edad" value={r.ageUnit} onChange={(e) => onPatch(r.key, { ageUnit: e.target.value as AgeBandUnit })} style={{ width: 110 }}>
            <option value="a">años</option>
            <option value="m">meses</option>
            <option value="d">días</option>
          </Select>
        </Field>
        <Field label="Condición" optional>
          <Input aria-label="Condición" placeholder="embarazo" value={r.condition} onChange={(e) => onPatch(r.key, { condition: e.target.value })} style={{ width: 140 }} />
        </Field>
        {bounds && (
          <>
            <Field label="Mínimo">
              <Input aria-label="Mínimo" inputMode="decimal" value={r.low} onChange={(e) => onPatch(r.key, { low: e.target.value })} style={{ width: 100 }} />
            </Field>
            <Field label="Máximo">
              <Input aria-label="Máximo" inputMode="decimal" value={r.high} onChange={(e) => onPatch(r.key, { high: e.target.value })} style={{ width: 100 }} />
            </Field>
          </>
        )}
        {analyte.kind !== "numeric" && (
          <Field label="Valor normal" optional={bounds}>
            <Input aria-label="Valor normal" value={r.expected} onChange={(e) => onPatch(r.key, { expected: e.target.value })} style={{ width: 160 }} />
          </Field>
        )}
        <IconButton size="sm" icon="x" label="Quitar valor de referencia" onClick={onRemove} />
      </div>
      <Field
        label="Texto en el informe"
        optional
        hint={
          preview?.includes("\n") ? (
            <span className="ref-hint">
              Se imprime: <RefText text={preview} />
            </span>
          ) : (
            `Se imprime: ${preview ?? "—"}`
          )
        }
      >
        <Textarea
          aria-label="Texto en el informe"
          placeholder="Vacío: se imprime el rango"
          rows={Math.min(6, Math.max(1, r.display.split("\n").length))}
          value={r.display}
          onChange={(e) => onPatch(r.key, { display: e.target.value, displayTouched: true })}
        />
      </Field>
      {error && <Alert tone="red">{error}</Alert>}
    </div>
  );
}
